import { useQuery } from "@tanstack/react-query";
import apiClient from "./client";
import { getTenantId } from "../utils/tenant";

const useConfigId = () => {
  const { data } = useQuery({
    queryKey: ['config-id'],
    queryFn: () => apiClient.config.getConfigBySlug(getTenantId()).then((res) => res.data._id),
  });

  return data || '';
}

export const useFetchSponsors = () => {
  const configId = useConfigId();
  return useQuery(["sponsors"], () => apiClient.sponsors.getSponsors().then((res) => res.data), {
    enabled: Boolean(configId),
  });
}

export const useFetchTournamentOptions = () => {
  const configId = useConfigId();
  return useQuery(["tournament-options"], () =>
    apiClient.config.getConfig(configId).then((res) => res.data.sport_settings), {
    enabled: Boolean(configId),
  }
  );
}


export const useFetchRanking = (selectedPhase: string | undefined) => {
  const configId = useConfigId();
  return useQuery(["ranking", selectedPhase], () =>
    apiClient.phases.getPhaseStandings(selectedPhase || "").then((res) => res.data),
    {
      enabled: Boolean(configId && selectedPhase),
    }
  );
}

export const useFetchGames = (selectedPhase: string | undefined) => {
  const configId = useConfigId();
  return useQuery(["game", selectedPhase], () =>
    apiClient.games.getGames({ phase_id: selectedPhase }).then((res) => res.data),
    {
      enabled: Boolean(configId && selectedPhase),
    }
  );
}

export const useFetchFields = () => {
  const configId = useConfigId();
  return useQuery(["fields"], () =>
    apiClient.fields.getFields().then((res) => res.data), {
    enabled: Boolean(configId),
  });
}

export const useFetchFieldGames = (params: {
  fieldId: string | undefined;
  phaseId?: string | undefined;
}) => {
  const configId = useConfigId();
  return useQuery(
    ["field-games", params.fieldId, params.phaseId],
    () =>
      apiClient.games
        .getGames({
          ...(params.fieldId && { field_id: params.fieldId }),
          ...(params.phaseId && { phase_id: params.phaseId }),
        })
        .then((res) => res.data),
    {
      staleTime: 30 * 1000,
      enabled: Boolean(configId && (params.fieldId || params.phaseId)),
    }
  );
}

export const useFetchFeatureFlags = () => {
  const configId = useConfigId();
  return useQuery(["feature-flags"], () =>
    apiClient.config.getConfig(configId).then((res) => ({
      is_live: res.data.is_live ?? false,
      show_phase_selector: res.data.show_phase_selector ?? false,
      pages: res.data.landing?.pages ?? ['teams', 'rankings', 'games', 'fields', 'stats'],
    })), {
    enabled: Boolean(configId),
  }
  );
}

export const useFetchTeamPersons = (teamId: string | undefined) => {
  const configId = useConfigId();
  return useQuery(
    ["team-persons", teamId],
    () => apiClient.teams.getTeam(teamId || "").then((res) => res.data.persons),
    {
      enabled: Boolean(configId && teamId),
    }
  );
};

export const useFetchGameActions = (gameId: string | undefined) => {
  const configId = useConfigId();
  return useQuery(
    ["game-actions", gameId],
    () => apiClient.actions.getActionsByGameId(gameId || "").then((res) => res.data),
    {
      enabled: Boolean(configId && gameId),
    }
  );
};

export const useFetchPlayersByStats = (
  stat: "tries" | "conversions" | "penalties" | "drops" | "points"
) => {
  const configId = useConfigId();
  // @TODO: Support stats on the player level
  return useQuery(["players-by-stats", stat], () =>
    apiClient.stats.getPlayersByStats({ stat }).then((res) => res.data), {
    enabled: Boolean(configId),
  });
};

export const useFetchTeamsByStats = (
  stat: "points_scored" | "points_taken",
  phaseId: string | undefined
) => {
  const configId = useConfigId();
  return useQuery(["teams-by-stats", stat, phaseId], () => apiClient.stats.getTeamsByStats({ stat, phaseId: phaseId || "" }).then((res) => res.data), {
    enabled: Boolean(configId && phaseId),
  });
};

export const useFetchTeams = () => {
  const configId = useConfigId();
  return useQuery(["teams"], () => apiClient.teams.getTeams().then((res) => res.data), {
    enabled: Boolean(configId),
  });
};

export const useFetchTeamById = (id: string | undefined) => {
  const configId = useConfigId();
  return useQuery(["team", id], () => apiClient.teams.getTeam(id || "").then((res) => res.data), {
    enabled: Boolean(configId && id),
  });
};

export const useFetchPhases = () => {
  const configId = useConfigId();
  return useQuery(["phases"], () => apiClient.phases.getPhases().then((res) => res.data), {
    enabled: Boolean(configId),
  });
};

export const useFetchConfig = () => {
  const configId = useConfigId();
  return useQuery(["config"], () => apiClient.config.getConfig(configId).then((res) => res.data), {
    enabled: Boolean(configId),
  });
}

export const useFetchAllConfigsDebug = () => {
  return useQuery(["all-configs-debug"], () => apiClient.config.getAllConfigsDebug().then((res) => res.data), {
    // @TODO: Enable it only when debug
    enabled: true
  });
}